// Smart Sash Admin Panel - Certification Transformers
// Functions to transform between API and UI data formats

import {
  CertificationAnswer,
  CertificationQuestion,
  CertificationQuestionParsed,
} from '@/models/certification.models';
import {
  ANSWER_IDS,
  REQUIRED_ANSWER_COUNT,
  DEFAULT_QUESTION_VALUES,
  createEmptyAnswer,
  createDefaultAnswers,
  AnswerId,
} from './certificationConstants';

/**
 * Result type for transformation operations
 */
export interface TransformResult<T> {
  success: boolean;
  data: T | null;
  error?: string;
}

/**
 * Parse answersJson string to CertificationAnswer array
 * Handles both formats:
 * - String array (API): ["Answer 1", "Answer 2"] → assign IDs a, b, c...
 * - Object array (legacy): [{id, text, isCorrect}] → use as-is
 * Returns default answers if parsing fails
 */
export const parseAnswersJson = (
  json: string | null | undefined
): TransformResult<CertificationAnswer[]> => {
  if (!json || json.trim() === '') {
    return {
      success: true,
      data: createDefaultAnswers(),
    };
  }

  try {
    const parsed = JSON.parse(json);

    if (!Array.isArray(parsed)) {
      return {
        success: false,
        data: createDefaultAnswers(),
        error: 'answersJson is not an array',
      };
    }

    // Detect format and normalize each answer
    const answers: CertificationAnswer[] = parsed.map((item, index) => {
      const id = ANSWER_IDS[index] || String(index);

      if (typeof item === 'string') {
        // API format: simple string array
        return { id, text: item, isCorrect: false };
      } else {
        // Legacy object format
        return {
          id: typeof item?.id === 'string' ? item.id : id,
          text: typeof item?.text === 'string' ? item.text : '',
          isCorrect: typeof item?.isCorrect === 'boolean' ? item.isCorrect : false,
        };
      }
    });

    return {
      success: true,
      data: normalizeAnswersArray(answers),
    };
  } catch (e) {
    return {
      success: false,
      data: createDefaultAnswers(),
      error: e instanceof Error ? e.message : 'Failed to parse answersJson',
    };
  }
};

/**
 * Convert CertificationAnswer array to JSON string
 * API expects array of strings, not objects
 */
export const stringifyAnswers = (answers: CertificationAnswer[]): string => {
  return JSON.stringify(answers.map((a) => a.text));
};

/**
 * Create default answers array with 8 empty answers (a-h)
 */
export const createDefaultAnswersArray = (): CertificationAnswer[] => {
  return createDefaultAnswers();
};

/**
 * Normalize answers array to ensure exactly 8 answers with correct IDs (a-h)
 */
export const normalizeAnswersArray = (answers: CertificationAnswer[]): CertificationAnswer[] => {
  const normalized: CertificationAnswer[] = [];

  for (let i = 0; i < REQUIRED_ANSWER_COUNT; i++) {
    const expectedId = ANSWER_IDS[i];
    const existing = answers.find((a) => a.id === expectedId) || answers[i];

    if (existing) {
      normalized.push({
        id: expectedId,
        text: existing.text || '',
        isCorrect: existing.isCorrect || false,
      });
    } else {
      normalized.push(createEmptyAnswer(expectedId));
    }
  }

  return normalized;
};

/**
 * Convert API format question to UI format (parsed answers)
 * Converts text-based correctAnswer from API to ID-based for UI
 */
export const questionToParsed = (
  question: CertificationQuestion
): TransformResult<CertificationQuestionParsed> => {
  const parseResult = parseAnswersJson(question.answersJson);

  // Sync isCorrect flags with correctAnswer field
  const answers = parseResult.data
    ? syncCorrectAnswer(parseResult.data, question.correctAnswer)
    : createDefaultAnswers();

  // Find the correct answer's ID (for UI consistency)
  const correctAnswer = answers.find((a) => a.isCorrect)?.id || question.correctAnswer;

  const { answersJson: _, ...rest } = question;

  return {
    success: parseResult.success,
    data: {
      ...rest,
      answers,
      correctAnswer,
    },
    error: parseResult.error,
  };
};

/**
 * Convert UI format question to API format (stringified answers)
 * Converts ID-based correctAnswer to text-based for API
 */
export const parsedToQuestion = (parsed: CertificationQuestionParsed): CertificationQuestion => {
  const { answers, correctAnswer, ...rest } = parsed;

  // Find the text of the correct answer (API expects text, not ID)
  const correctAnswerText = answers.find((a) => a.id === correctAnswer)?.text || correctAnswer;

  return {
    ...rest,
    answersJson: stringifyAnswers(answers),
    correctAnswer: correctAnswerText,
  };
};

/**
 * Batch convert multiple API questions to UI format
 */
export const batchQuestionsToParsed = (
  questions: CertificationQuestion[]
): TransformResult<CertificationQuestionParsed[]> => {
  const results = questions.map(questionToParsed);
  const hasErrors = results.some((r) => !r.success);
  const data = results.map((r) => r.data!);

  return {
    success: !hasErrors,
    data,
    error: hasErrors ? 'Some questions failed to parse' : undefined,
  };
};

/**
 * Batch convert multiple UI questions to API format
 */
export const batchParsedToQuestions = (
  parsed: CertificationQuestionParsed[]
): CertificationQuestion[] => {
  return parsed.map(parsedToQuestion);
};

/**
 * Create a new empty question with default values
 */
export const createEmptyQuestion = (institutionId: string): CertificationQuestionParsed => {
  return {
    id: '',
    institutionId,
    question: DEFAULT_QUESTION_VALUES.question,
    questionType: DEFAULT_QUESTION_VALUES.questionType,
    answers: createDefaultAnswers(),
    correctAnswer: DEFAULT_QUESTION_VALUES.correctAnswer,
    points: DEFAULT_QUESTION_VALUES.points,
    isActive: DEFAULT_QUESTION_VALUES.isActive,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

/**
 * Sync isCorrect flags in answers array with correctAnswer
 * Handles both ID-based (a-h) and text-based matching (API format)
 */
export const syncCorrectAnswer = (
  answers: CertificationAnswer[],
  correctId: string | null | undefined
): CertificationAnswer[] => {
  if (!correctId) return answers;

  // Check if correctId is an ID (a-h) or answer text
  const isIdBased = ANSWER_IDS.includes(correctId as AnswerId);

  return answers.map((answer) => ({
    ...answer,
    isCorrect: isIdBased ? answer.id === correctId : answer.text === correctId,
  }));
};

/**
 * Deep clone a single question (for backup/undo purposes)
 */
export const cloneQuestion = (
  question: CertificationQuestionParsed
): CertificationQuestionParsed => {
  return {
    ...question,
    answers: question.answers.map((answer) => ({ ...answer })),
  };
};

/**
 * Deep clone an array of questions
 */
export const cloneQuestions = (
  questions: CertificationQuestionParsed[]
): CertificationQuestionParsed[] => {
  return questions.map(cloneQuestion);
};

/**
 * Get the correct answer object from a parsed question
 */
export const getCorrectAnswerFromQuestion = (
  question: CertificationQuestionParsed
): CertificationAnswer | undefined => {
  return question.answers.find((a) => a.id === question.correctAnswer);
};

/**
 * Update a specific answer in a question
 */
export const updateAnswerInQuestion = (
  question: CertificationQuestionParsed,
  answerId: string,
  updates: Partial<Omit<CertificationAnswer, 'id'>>
): CertificationQuestionParsed => {
  return {
    ...question,
    answers: question.answers.map((answer) =>
      answer.id === answerId ? { ...answer, ...updates } : answer
    ),
  };
};

/**
 * Set the correct answer for a question
 */
export const setCorrectAnswer = (
  question: CertificationQuestionParsed,
  answerId: string
): CertificationQuestionParsed => {
  return {
    ...question,
    correctAnswer: answerId,
    answers: syncCorrectAnswer(question.answers, answerId),
  };
};